/**
 * PhoneField component.
 *
 * Phone number input with country code selector dropdown.
 * Fetches available country codes from the backend API.
 * Default: Singapore (+65). Validates per-country format.
 */

import type { FormFieldDefinition } from "../../types";

/** Fallback country codes when API is unavailable */
const FALLBACK_CODES = [
  { code: "SG", dial_code: "+65", country_name: "Singapore" },
  { code: "MY", dial_code: "+60", country_name: "Malaysia" },
  { code: "ID", dial_code: "+62", country_name: "Indonesia" },
  { code: "IN", dial_code: "+91", country_name: "India" },
  { code: "CN", dial_code: "+86", country_name: "China" },
  { code: "PH", dial_code: "+63", country_name: "Philippines" },
  { code: "AU", dial_code: "+61", country_name: "Australia" },
  { code: "GB", dial_code: "+44", country_name: "United Kingdom" },
  { code: "US", dial_code: "+1", country_name: "United States" },
];

interface CountryCode {
  code: string;
  dial_code: string;
  country_name: string;
}

export interface PhoneFieldOptions {
  field: FormFieldDefinition;
  onChange: (name: string, value: string) => void;
  /** API base URL (e.g. "https://hana.kailash.ai") for fetching country codes */
  apiBaseUrl?: string;
}

export class PhoneField {
  private readonly element: HTMLElement;
  private readonly inputEl: HTMLInputElement;
  private readonly errorEl: HTMLElement;
  private readonly selectEl: HTMLSelectElement;
  private selectedCountryCode: string;

  constructor(private readonly options: PhoneFieldOptions) {
    const { field } = options;

    // Use country_code from field definition if provided, else default +65
    this.selectedCountryCode = (field as any).country_code || "+65";

    this.element = document.createElement("div");
    this.element.className = "hana-form-field";

    // Label
    const labelEl = document.createElement("label");
    labelEl.className = "hana-form-field__label";
    labelEl.setAttribute("for", `hana-field-${field.name}`);
    labelEl.textContent = field.label;
    if (field.required) {
      const asterisk = document.createElement("span");
      asterisk.className = "hana-form-field__required";
      asterisk.textContent = " *";
      asterisk.setAttribute("aria-hidden", "true");
      labelEl.appendChild(asterisk);
    }
    this.element.appendChild(labelEl);

    // Input wrapper for select + input
    const wrapperEl = document.createElement("div");
    wrapperEl.className = "hana-form-field__phone-wrapper";

    // Country code dropdown
    this.selectEl = document.createElement("select");
    this.selectEl.className = "hana-form-field__phone-select";
    this.selectEl.setAttribute("aria-label", "Country code");
    this._populateSelect(FALLBACK_CODES);
    this.selectEl.value = this.selectedCountryCode;

    this.selectEl.addEventListener("change", () => {
      this.selectedCountryCode = this.selectEl.value;
      this._updatePlaceholder();
      this.clearError();
      this.options.onChange(field.name, this.getFullValue());
    });

    wrapperEl.appendChild(this.selectEl);

    // Phone input
    this.inputEl = document.createElement("input");
    this.inputEl.type = "tel";
    this.inputEl.id = `hana-field-${field.name}`;
    this.inputEl.className =
      "hana-form-field__input hana-form-field__input--phone";
    this.inputEl.name = field.name;
    this.inputEl.setAttribute(
      "aria-label",
      field.label + (field.required ? " (required)" : ""),
    );
    if (field.required) {
      this.inputEl.setAttribute("aria-required", "true");
    }
    this.inputEl.inputMode = "numeric";
    this._updatePlaceholder();

    if (field.value) {
      // Strip any prefix if the backend sends it
      const stripped = field.value.replace(/^\+\d{1,3}\s*/, "");
      this.inputEl.value = stripped;
    }

    this.inputEl.addEventListener("input", () => {
      // Allow only digits
      this.inputEl.value = this.inputEl.value.replace(/\D/g, "");
      this.clearError();
      this.options.onChange(field.name, this.getFullValue());
    });

    wrapperEl.appendChild(this.inputEl);
    this.element.appendChild(wrapperEl);

    // Error message
    this.errorEl = document.createElement("div");
    this.errorEl.className = "hana-form-field__error";
    this.errorEl.setAttribute("role", "alert");
    this.errorEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.errorEl);

    // Fetch country codes from API (non-blocking)
    this._fetchCountryCodes();
  }

  getElement(): HTMLElement {
    return this.element;
  }

  /** Returns the full phone number with country code (e.g., "+65 91234567") */
  getValue(): string {
    return this.getFullValue();
  }

  /** Returns just the digits without prefix */
  getRawValue(): string {
    return this.inputEl.value.trim();
  }

  /** Returns the selected country code (e.g., "+65") */
  getCountryCode(): string {
    return this.selectedCountryCode;
  }

  private getFullValue(): string {
    const raw = this.getRawValue();
    if (raw.length === 0) return "";
    return `${this.selectedCountryCode} ${raw}`;
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
    this.inputEl.classList.add("hana-form-field__input--error");
    this.inputEl.setAttribute("aria-invalid", "true");
  }

  clearError(): void {
    this.errorEl.textContent = "";
    this.inputEl.classList.remove("hana-form-field__input--error");
    this.inputEl.removeAttribute("aria-invalid");
  }

  setDisabled(disabled: boolean): void {
    this.inputEl.disabled = disabled;
    this.selectEl.disabled = disabled;
  }

  focus(): void {
    this.inputEl.focus();
  }

  private _updatePlaceholder(): void {
    if (this.selectedCountryCode === "+65") {
      this.inputEl.placeholder = "8XXX XXXX";
      this.inputEl.maxLength = 8;
    } else if (this.selectedCountryCode === "+60") {
      this.inputEl.placeholder = "1X XXXX XXXX";
      this.inputEl.maxLength = 10;
    } else {
      this.inputEl.placeholder = "Phone number";
      this.inputEl.maxLength = 15;
    }
  }

  private _populateSelect(codes: CountryCode[]): void {
    this.selectEl.innerHTML = "";
    for (const c of codes) {
      const opt = document.createElement("option");
      opt.value = c.dial_code;
      opt.textContent = `${c.code} ${c.dial_code}`;
      this.selectEl.appendChild(opt);
    }
  }

  private async _fetchCountryCodes(): Promise<void> {
    try {
      const baseUrl = this.options.apiBaseUrl || "";
      const resp = await fetch(`${baseUrl}/api/chat/country-codes`);
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data) && data.length > 0) {
          this._populateSelect(data);
          this.selectEl.value = this.selectedCountryCode;
        }
      }
    } catch {
      // Fallback codes already populated
    }
  }
}
